import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Textarea } from "../ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { ScrollArea } from "../ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Calendar,
  Clock,
  BookOpen,
  Target,
  Users,
  FileText,
  Plus,
  X,
  Loader2,
  GraduationCap,
  Lightbulb,
  Link2,
  Unlink,
  Upload,
  Paperclip,
  ExternalLink,
  Trash2,
} from "lucide-react";
import { MATIERES, NIVEAUX, SESSION_TYPES, getMatiereInfo } from "./constants";
import { DynamicSubjectSelector } from "./DynamicSubjectSelector";
import { SkillSelector, ResourceSelector, StudentSelector } from "./session-editor";
import {
  createSession,
  updateSession,
  listLessonPlans,
  linkLessonPlanToSession,
  unlinkLessonPlanFromSession,
  uploadFile,
  getFileUrl,
} from "../../lib/api";
import { toast } from "sonner";

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
};

export function SessionEditorModal({
  open,
  onOpenChange,
  session = null,
  selectedDate,
  selectedSlot,
  timeSlots = [],
  onSave,
  teacherCycles = [], // Cycles du profil enseignant
}) {
  const isEdit = !!session;
  const [activeTab, setActiveTab] = useState("general");
  const [saving, setSaving] = useState(false);

  // Form state
  const [title, setTitle] = useState("");
  const [matiere, setMatiere] = useState("");
  const [matiereLabel, setMatiereLabel] = useState("");
  const [matiereColor, setMatiereColor] = useState("");
  const [niveau, setNiveau] = useState("");
  const [sessionType, setSessionType] = useState("entrainement");
  const [date, setDate] = useState("");
  const [timeSlotId, setTimeSlotId] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [notes, setNotes] = useState("");
  const [materials, setMaterials] = useState("");
  const [homework, setHomework] = useState("");

  // Skills
  const [skills, setSkills] = useState([]);

  // Learning goals
  const [learningGoals, setLearningGoals] = useState([]);
  const [newGoal, setNewGoal] = useState("");

  // Resource
  const [resourceId, setResourceId] = useState(null);
  const [resourceTitle, setResourceTitle] = useState(null);
  const [suggestedSkills, setSuggestedSkills] = useState([]);

  // Lesson Plan (Fiche de prép)
  const [lessonPlanId, setLessonPlanId] = useState(null);
  const [lessonPlanTitle, setLessonPlanTitle] = useState(null);
  const [availableLessonPlans, setAvailableLessonPlans] = useState([]);
  const [showLessonPlanSelector, setShowLessonPlanSelector] = useState(false);

  // Differentiation
  const [targetType, setTargetType] = useState("whole_class");
  const [targetStudentIds, setTargetStudentIds] = useState([]);
  const [hasDifferentiation, setHasDifferentiation] = useState(false);

  // Attached Documents
  const [attachedUrls, setAttachedUrls] = useState([]);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [newUrl, setNewUrl] = useState("");
  const [newUrlTitle, setNewUrlTitle] = useState("");
  const [uploadingFile, setUploadingFile] = useState(false);

  useEffect(() => {
    if (session) {
      setTitle(session.title || "");
      setMatiere(session.matiere || "");
      setMatiereLabel(session.matiere_label || "");
      setMatiereColor(session.matiere_color || "");
      setNiveau(session.niveau || "");
      setSessionType(session.session_type || "entrainement");
      setDate(session.date || "");
      setTimeSlotId(session.time_slot_id || "");
      setStartTime(session.start_time || "");
      setEndTime(session.end_time || "");
      setNotes(session.notes || "");
      setMaterials(session.materials || "");
      setHomework(session.homework || "");
      setSkills(session.skills || []);
      setLearningGoals(session.learning_goals || []);
      setResourceId(session.resource_id || null);
      setResourceTitle(session.resource_title || null);
      setTargetType(session.target_type || "whole_class");
      setTargetStudentIds(session.target_student_ids || []);
      setHasDifferentiation(session.has_differentiation || false);
      setLessonPlanId(session.lesson_plan_id || null);
      setLessonPlanTitle(session.lesson_plan_title || null);
      setAttachedUrls(session.attached_urls || []);
      setAttachedFiles(session.attached_files || []);
    } else {
      // New session
      setTitle("");
      setMatiere("");
      setMatiereLabel("");
      setMatiereColor("");
      setNiveau("");
      setSessionType("entrainement");
      setDate(selectedDate || "");
      if (selectedSlot) {
        const slot = timeSlots.find((s) => s.id === selectedSlot);
        if (slot) {
          setTimeSlotId(slot.id);
          setStartTime(slot.start_time);
          setEndTime(slot.end_time);
        }
      }
      setNotes("");
      setMaterials("");
      setHomework("");
      setSkills([]);
      setLearningGoals([]);
      setResourceId(null);
      setResourceTitle(null);
      setTargetType("whole_class");
      setTargetStudentIds([]);
      setHasDifferentiation(false);
      setSuggestedSkills([]);
      setLessonPlanId(null);
      setLessonPlanTitle(null);
      setAttachedUrls([]);
      setAttachedFiles([]);
      setNewUrl("");
      setNewUrlTitle("");
    }
  }, [session, selectedDate, selectedSlot, timeSlots]);

  // Fetch available lesson plans
  useEffect(() => {
    const fetchLessonPlans = async () => {
      try {
        const { data } = await listLessonPlans({ status: "ready", limit: 50 });
        setAvailableLessonPlans(data.lesson_plans || []);
      } catch (err) {
        console.error("Error fetching lesson plans:", err);
      }
    };
    if (open) {
      fetchLessonPlans();
    }
  }, [open]);

  // Fetch skill suggestions when resource changes
  useEffect(() => {
    const fetchResourceSkills = async () => {
      if (!resourceId) {
        setSuggestedSkills([]);
        return;
      }
      
      try {
        // Get the resource to check if it has skills
        const { data: resources } = await listResources({ limit: 100 });
        const resource = resources.find(r => r.id === resourceId);
        
        if (resource?.skills && resource.skills.length > 0) {
          setSuggestedSkills(resource.skills);
          toast.info("Suggestions de competences disponibles", {
            description: "Basees sur la ressource selectionnee",
            duration: 3000,
          });
        } else {
          setSuggestedSkills([]);
        }
      } catch (err) {
        console.error("Error fetching resource skills:", err);
      }
    };
    
    fetchResourceSkills();
  }, [resourceId]);

  const handleTimeSlotChange = (slotId) => {
    setTimeSlotId(slotId);
    const slot = timeSlots.find((s) => s.id === slotId);
    if (slot) {
      setStartTime(slot.start_time);
      setEndTime(slot.end_time);
    }
  };

  const addLearningGoal = () => {
    if (!newGoal.trim()) return;
    setLearningGoals([
      ...learningGoals,
      { id: `goal_${Date.now()}`, text: newGoal.trim(), evaluation_criteria: null },
    ]);
    setNewGoal("");
  };

  const removeLearningGoal = (goalId) => {
    setLearningGoals(learningGoals.filter((g) => g.id !== goalId));
  };

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error("Le titre est obligatoire");
      return;
    }
    if (!matiere) {
      toast.error("La matière est obligatoire");
      return;
    }
    if (!date) {
      toast.error("La date est obligatoire");
      return;
    }

    setSaving(true);
    try {
      const sessionData = {
        date,
        time_slot_id: timeSlotId || null,
        start_time: startTime || null,
        end_time: endTime || null,
        matiere,
        matiere_label: matiereLabel || null,
        matiere_color: matiereColor || null,
        niveau: niveau || null,
        title: title.trim(),
        session_type: sessionType,
        resource_id: resourceId,
        resource_title: resourceTitle,
        lesson_plan_id: lessonPlanId,
        lesson_plan_title: lessonPlanTitle,
        skills,
        learning_goals: learningGoals,
        target_type: targetType,
        target_student_ids: targetStudentIds,
        has_differentiation: hasDifferentiation,
        materials: materials || null,
        notes: notes || null,
        homework: homework || null,
        attached_urls: attachedUrls,
        attached_files: attachedFiles,
        status: session?.status || "prevu",
      };

      let savedSession;
      if (isEdit) {
        savedSession = await updateSession(session.id, sessionData);
        toast.success("Séance modifiée");
      } else {
        savedSession = await createSession(sessionData);
        toast.success("Séance créée");
      }

      // Link lesson plan if specified
      if (lessonPlanId && savedSession?.data?.id) {
        try {
          await linkLessonPlanToSession(lessonPlanId, savedSession.data.id);
        } catch (err) {
          console.error("Failed to link lesson plan:", err);
        }
      }

      if (onSave) onSave();
      onOpenChange(false);
    } catch (err) {
      console.error("Error saving session:", err);
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  // Get matiere info for display (fallback to static list if dynamic not found)
  const matiereInfo = matiereColor 
    ? { color: matiereColor, label: matiereLabel }
    : getMatiereInfo(matiere);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {matiereInfo?.icon && <matiereInfo.icon className="w-5 h-5" style={{ color: matiereInfo.color }} />}
            {isEdit ? "Modifier la séance" : "Nouvelle séance"}
          </DialogTitle>
          <DialogDescription>
            {isEdit 
              ? "Modifiez les détails de cette séance de cours"
              : "Planifiez une nouvelle séance et liez-la aux compétences officielles"
            }
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="general" className="text-xs">
              <Calendar className="w-3 h-3 mr-1" />
              General
            </TabsTrigger>
            <TabsTrigger value="skills" className="text-xs">
              <Target className="w-3 h-3 mr-1" />
              Competences
            </TabsTrigger>
            <TabsTrigger value="resource" className="text-xs">
              <FileText className="w-3 h-3 mr-1" />
              Ressource
            </TabsTrigger>
            <TabsTrigger value="documents" className="text-xs">
              <Paperclip className="w-3 h-3 mr-1" />
              Documents
            </TabsTrigger>
            <TabsTrigger value="students" className="text-xs">
              <Users className="w-3 h-3 mr-1" />
              Élèves
            </TabsTrigger>
          </TabsList>

          <ScrollArea className="flex-1 mt-4">
            <div className="px-1">
              <TabsContent value="general" className="mt-0 space-y-4">
                {/* Date & Time */}
                <div className="flex items-center gap-2 text-sm text-slate-500 bg-slate-50 p-2 rounded-lg">
                  <Calendar className="w-4 h-4" />
                  <Input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="h-8 w-[140px]"
                  />
                  <Clock className="w-4 h-4 ml-2" />
                  <Select value={timeSlotId} onValueChange={handleTimeSlotChange}>
                    <SelectTrigger className="h-8 w-[150px]">
                      <SelectValue placeholder="Creneau" />
                    </SelectTrigger>
                    <SelectContent>
                      {timeSlots.map((slot) => (
                        <SelectItem key={slot.id} value={slot.id}>
                          {slot.label} ({slot.start_time}-{slot.end_time})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Title */}
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1 block">Titre</label>
                  <Input
                    placeholder="Ex: Lecture - Le petit chaperon rouge"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                  />
                </div>

                {/* Matiere, Niveau, Type */}
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs font-medium text-slate-500 mb-1 block">Matière *</label>
                    <DynamicSubjectSelector
                      value={matiere}
                      onChange={(id, subject) => {
                        setMatiere(id);
                        setMatiereLabel(subject?.label || "");
                        setMatiereColor(subject?.color || "");
                      }}
                      cycles={teacherCycles.length > 0 ? teacherCycles : undefined}
                      niveau={niveau || "ce1"}
                      placeholder="Matière"
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-500 mb-1 block">Niveau</label>
                    <Select value={niveau} onValueChange={setNiveau}>
                      <SelectTrigger>
                        <SelectValue placeholder="Niveau" />
                      </SelectTrigger>
                      <SelectContent>
                        {NIVEAUX.map((n) => (
                          <SelectItem key={n.value} value={n.value}>
                            {n.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-500 mb-1 block">Type</label>
                    <Select value={sessionType} onValueChange={setSessionType}>
                      <SelectTrigger>
                        <SelectValue placeholder="Type" />
                      </SelectTrigger>
                      <SelectContent>
                        {SESSION_TYPES.map((t) => (
                          <SelectItem key={t.value} value={t.value}>
                            {t.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Fiche de prép liée */}
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1 block">
                    <FileText className="w-3 h-3 inline mr-1" />
                    Fiche de préparation
                  </label>
                  {lessonPlanId ? (
                    <div className="flex items-center gap-2 p-2 bg-indigo-50 border border-indigo-200 rounded-lg">
                      <Link2 className="w-4 h-4 text-indigo-600" />
                      <span className="flex-1 text-sm font-medium text-indigo-800">
                        {lessonPlanTitle || "Fiche liée"}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-indigo-600 hover:text-indigo-800"
                        onClick={() => {
                          setLessonPlanId(null);
                          setLessonPlanTitle(null);
                        }}
                      >
                        <Unlink className="w-3 h-3 mr-1" />
                        Délier
                      </Button>
                    </div>
                  ) : (
                    <div>
                      {showLessonPlanSelector ? (
                        <div className="space-y-2">
                          <ScrollArea className="h-32 border rounded-lg p-2">
                            {availableLessonPlans.length === 0 ? (
                              <p className="text-sm text-slate-400 text-center py-4">
                                Aucune fiche de prép disponible
                              </p>
                            ) : (
                              <div className="space-y-1">
                                {availableLessonPlans.map((plan) => (
                                  <div
                                    key={plan.id}
                                    className="flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-slate-50"
                                    onClick={() => {
                                      setLessonPlanId(plan.id);
                                      setLessonPlanTitle(plan.title);
                                      setShowLessonPlanSelector(false);
                                      // Auto-remplir les objectifs si la fiche en a
                                      if (plan.objectives?.length > 0 && learningGoals.length === 0) {
                                        setLearningGoals(plan.objectives.map((obj, i) => ({
                                          id: `goal_${Date.now()}_${i}`,
                                          text: obj
                                        })));
                                      }
                                      toast.success("Fiche de prép liée");
                                    }}
                                  >
                                    <FileText className="w-4 h-4 text-indigo-500" />
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-medium truncate">{plan.title}</p>
                                      <p className="text-xs text-slate-500">
                                        {plan.domain_label} • {plan.duration_minutes} min
                                      </p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </ScrollArea>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowLessonPlanSelector(false)}
                          >
                            Annuler
                          </Button>
                        </div>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full justify-start text-slate-500"
                          onClick={() => setShowLessonPlanSelector(true)}
                        >
                          <Link2 className="w-4 h-4 mr-2" />
                          Lier une fiche de préparation
                        </Button>
                      )}
                    </div>
                  )}
                </div>

                {/* Learning Goals */}
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1 block">
                    <Lightbulb className="w-3 h-3 inline mr-1" />
                    Objectifs d'apprentissage
                  </label>
                  {learningGoals.length > 0 && (
                    <div className="space-y-1 mb-2">
                      {learningGoals.map((goal) => (
                        <div
                          key={goal.id}
                          className="flex items-center gap-2 text-sm bg-green-50 text-green-800 px-2 py-1 rounded"
                        >
                          <span className="flex-1">{goal.text}</span>
                          <X
                            className="w-3 h-3 cursor-pointer hover:text-red-500"
                            onClick={() => removeLearningGoal(goal.id)}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Input
                      placeholder="Ajouter un objectif..."
                      value={newGoal}
                      onChange={(e) => setNewGoal(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && addLearningGoal()}
                      className="h-8"
                    />
                    <Button size="sm" variant="outline" onClick={addLearningGoal} className="h-8">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1 block">Notes / Description</label>
                  <Textarea
                    placeholder="Notes pour cette séance..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={2}
                  />
                </div>

                {/* Materials & Homework */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-slate-500 mb-1 block">Materiel</label>
                    <Input
                      placeholder="Cahier, manuel p.12..."
                      value={materials}
                      onChange={(e) => setMaterials(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-500 mb-1 block">Devoirs</label>
                    <Input
                      placeholder="Ex p.45 n°3"
                      value={homework}
                      onChange={(e) => setHomework(e.target.value)}
                    />
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="skills" className="mt-0">
                <SkillSelector
                  selectedSkills={skills}
                  onSkillsChange={setSkills}
                  matiere={matiere}
                  niveau={niveau}
                  suggestedSkills={suggestedSkills}
                />
              </TabsContent>

              <TabsContent value="resource" className="mt-0">
                <ResourceSelector
                  selectedResourceId={resourceId}
                  onResourceChange={(id, title) => {
                    setResourceId(id);
                    setResourceTitle(title);
                  }}
                />
              </TabsContent>

              <TabsContent value="documents" className="mt-0 space-y-4">
                {/* URL Links */}
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-2 block flex items-center gap-1">
                    <ExternalLink className="w-3 h-3" />
                    Liens externes
                  </label>
                  <div className="space-y-2">
                    {attachedUrls.map((urlItem, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                        <ExternalLink className="w-4 h-4 text-slate-400" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{urlItem.title || urlItem.url}</p>
                          <p className="text-xs text-slate-400 truncate">{urlItem.url}</p>
                        </div>
                        <a
                          href={urlItem.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-indigo-600 hover:text-indigo-800"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                          onClick={() => {
                            setAttachedUrls(prev => prev.filter((_, i) => i !== idx));
                          }}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                    <div className="flex gap-2">
                      <Input
                        placeholder="Titre (optionnel)"
                        value={newUrlTitle}
                        onChange={(e) => setNewUrlTitle(e.target.value)}
                        className="h-8 w-1/3"
                      />
                      <Input
                        placeholder="https://..."
                        value={newUrl}
                        onChange={(e) => setNewUrl(e.target.value)}
                        className="h-8 flex-1"
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!newUrl.trim()}
                        onClick={() => {
                          if (newUrl.trim()) {
                            setAttachedUrls(prev => [...prev, {
                              url: newUrl.trim(),
                              title: newUrlTitle.trim() || newUrl.trim()
                            }]);
                            setNewUrl("");
                            setNewUrlTitle("");
                          }
                        }}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Uploaded Files */}
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-2 block flex items-center gap-1">
                    <Upload className="w-3 h-3" />
                    Fichiers téléversés
                  </label>
                  <div className="space-y-2">
                    {attachedFiles.map((file, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                        <FileText className="w-4 h-4 text-slate-400" />
                        <span className="flex-1 text-sm truncate">{file.name}</span>
                        <a
                          href={getFileUrl(file.id)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-indigo-600 hover:text-indigo-800"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                          onClick={() => {
                            setAttachedFiles(prev => prev.filter((_, i) => i !== idx));
                          }}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                    <label className="flex items-center gap-2 p-3 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-indigo-400 transition-colors">
                      {uploadingFile ? (
                        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                      ) : (
                        <Upload className="w-5 h-5 text-slate-400" />
                      )}
                      <span className="text-sm text-slate-500">
                        {uploadingFile ? "Téléversement..." : "Ajouter un fichier (PDF, image, doc...)"}
                      </span>
                      <input
                        type="file"
                        className="hidden"
                        disabled={uploadingFile}
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          setUploadingFile(true);
                          try {
                            const res = await uploadFile(file, "session-documents");
                            setAttachedFiles(prev => [...prev, {
                              id: res.data.id,
                              name: res.data.filename,
                              url: res.data.url
                            }]);
                            toast.success("Fichier ajouté");
                          } catch (err) {
                            console.error("Upload error:", err);
                            toast.error("Erreur lors du téléversement");
                          } finally {
                            setUploadingFile(false);
                            e.target.value = "";
                          }
                        }}
                      />
                    </label>
                  </div>
                </div>

                {/* Summary */}
                {(attachedUrls.length > 0 || attachedFiles.length > 0) && (
                  <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                    <p className="text-sm text-indigo-700">
                      <Paperclip className="w-4 h-4 inline mr-1" />
                      {attachedUrls.length + attachedFiles.length} document(s) attaché(s) à cette séance
                    </p>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="students" className="mt-0">
                <StudentSelector
                  targetType={targetType}
                  selectedStudentIds={targetStudentIds}
                  onTargetChange={setTargetType}
                  onStudentsChange={setTargetStudentIds}
                />
                {targetType !== "whole_class" && targetStudentIds.length > 0 && resourceId && (
                  <div className="mt-4 p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="differentiation"
                        checked={hasDifferentiation}
                        onCheckedChange={setHasDifferentiation}
                      />
                      <label htmlFor="differentiation" className="text-sm cursor-pointer">
                        Générer des variantes différenciées pour ces élèves
                      </label>
                    </div>
                    <p className="text-xs text-slate-500 mt-1 ml-6">
                      Les variantes seront adaptées aux préférences d'affichage de chaque élève
                    </p>
                  </div>
                )}
              </TabsContent>
            </div>
          </ScrollArea>
        </Tabs>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Enregistrement...
              </>
            ) : isEdit ? (
              "Enregistrer"
            ) : (
              "Créer la séance"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
